import { ConcurrencySweepChart } from '@/components/charts';
import type { Slide } from '@/components/investor';

/**
 * Fnk seed pitch. Mirrors docs/investors/fnk/pitch.md; every figure comes from
 * docs/business-plan/fnk-business-plan.md — keep the three in sync.
 */
export const pitchSlides: Slide[] = [
  {
    nav: 'Cover',
    kicker: 'Fnk · Seed pitch',
    title: 'The AI Stack, the Harness and the Inference Underneath Enterprise AI.',
    lead: 'Fnk turns open-weight models into a governed, private, efficient AI service. **The Fnk AI stack** — agent framework, control layer and reactive inference engine, built as one non-blocking system — and **the Fnk Harness**, the configured layer of workflows, tools, policies and evaluation sets. Licensed per GPU on a buyer’s own hardware, or delivered as a complete box.',
    blocks: [
      {
        kind: 'metrics',
        items: [
          { value: '2.0×', label: 'Completed requests per second over Spring MVC + LangChain4j + vLLM, same NVIDIA GPU' },
          { value: '1.4× → 2.0×', label: 'Lead at every point of a five-point concurrency sweep' },
          { value: '≈87%', label: 'Gross margin on every recurring year of a box' },
          { value: '0', label: 'Paying customers. Every commercial figure is a scenario', tone: 'muted' },
        ],
      },
      {
        kind: 'cards',
        items: [
          { title: 'First customer — DialogMe', text: 'A conversational AI platform running its production workload on the Fnk stack.' },
          { title: 'Stage — TRL 6, pre-revenue', text: 'Stack working across all three layers, benchmark passed, a production workload on it. Built on ≈€23,000.' },
        ],
      },
    ],
  },
  {
    nav: 'Vision',
    kicker: 'Vision, mission and positioning',
    title: 'Enterprise AI, Built as One System',
    blocks: [
      {
        kind: 'matrix',
        head: ['', 'Statement'],
        rows: [
          ['Vision', 'LLMs are replacing buttons and forms as the interface to services; behind every conversation runs an agentic workload that automates the service and makes it intelligent — Fnk builds the tools and the product that workload runs on'],
          ['Mission', 'Turn open-weight models into a governed, private and efficient AI service on hardware the customer controls — and prove every claim on the customer’s own workload'],
          ['Category', 'Enterprise AI infrastructure — the layers under the application'],
        ],
        accentRows: [0],
      },
      {
        kind: 'matrix',
        head: ['Buyer', 'The line', 'Form of sale'],
        rows: [
          ['Inference providers, data centres, GPU estates, OEMs', '*More completed work from every GPU you already run*', 'Stack licence, per GPU per year'],
          ['Regulated enterprises without a platform team', '*Private AI without a platform team*', 'Partial or full box'],
          ['Teams that want the stack without running GPUs', '*The same stack, operated for you*', 'SaaS subscription'],
        ],
        minWidth: '560px',
      },
    ],
  },
  {
    nav: 'Problem',
    kicker: 'Problem',
    title: 'Nine Disciplines, and Accelerators Waiting on Software',
    blocks: [
      {
        kind: 'cards',
        cols: 2,
        items: [
          { title: 'Enterprises cannot assemble private AI', text: 'Inference serving, model lifecycle, orchestration, retrieval, identity, business systems, observability, governance and operations — nine disciplines. A mid-market enterprise has one or two.' },
          { title: 'So they send data out, or do nothing', text: 'Confidential content goes to a third-country API against their own policy — or the project never leaves the pilot.' },
          { title: 'Operators overpay for accelerators', text: 'The standard stack bolts a LangChain-class framework onto vLLM across an HTTP boundary that carries neither capacity upward nor cancellation downward.' },
          { title: 'Procurement cannot sign a reference architecture', text: 'Four vendors and an internal team point at each other; there is no one accountable for the outcome.' },
        ],
      },
      {
        kind: 'callout',
        title: 'The waste is architectural',
        text: 'A framework that cannot see runtime capacity and an engine that cannot see the business transaction will always overprovision, always tail-collapse under load, and always pay for work nobody is waiting for.',
      },
    ],
  },
  {
    nav: 'Product',
    kicker: 'The product',
    title: 'The Stack, the Harness, and How to Buy Them',
    blocks: [
      {
        kind: 'matrix',
        head: ['Form', 'Price', 'What it is', 'Who it is for'],
        rows: [
          ['SaaS subscription', 'Monthly or annual', 'The Fnk stack operated by Fnk — framework, control layer and inference engine as a managed service', 'Teams that want the stack without running GPUs'],
          ['Stack licence', 'Per GPU per year', 'Framework, control layer and inference engine, on the buyer’s own GPUs', 'Inference providers, data centres, enterprise GPU estates, OEMs'],
          ['Partial box', '≈€200k year one · €145k recurring', 'Stack, Harness, connectors and console on the customer’s hardware', 'Enterprises with a data centre'],
          ['Full box', '≈€290k year one · €150k recurring', 'The same, on licensed hardware, air-gapped if required', 'Regulated organisations without a platform team'],
        ],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: '**Both boxes are the same build**, differing only in who owns the hardware. **DialogMe**, Fnk’s first customer, runs its production workload on the same stack.',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'What Fnk is deliberately not',
        text: 'It does not train foundation models. The integration runs from the service down to the serving engine and **stops at the model weights**, which are open-weight and third-party.',
      },
    ],
  },
  {
    nav: 'Technical win',
    kicker: 'The technical win',
    title: 'One Non-Blocking System, Not Two Joined by HTTP',
    blocks: [
      {
        kind: 'matrix',
        head: ['', 'What the Fnk stack does that the assembled stack cannot'],
        rows: [
          ['Bidirectional capacity contract', 'The engine signals capacity upward; the framework stops admitting work a saturated runtime cannot finish'],
          ['End-to-end cancellation with reclaim accounting', 'When a user walks away, token generation stops — and the reclaimed compute is counted'],
          ['Business-transaction budgets', 'Token, tool, call and time limits for a whole transaction, not per request — agent fan-out stays bounded'],
          ['Replay-based sizing', 'The next accelerator purchase is sized against recorded load, not a peak guess'],
        ],
        minWidth: '480px',
      },
      {
        kind: 'matrix',
        head: ['Platform', 'Evidence today', 'Role'],
        rows: [
          ['NVIDIA / CUDA', 'The paired benchmark on the next slide — TensorRT-LLM', 'The data-centre path and default box'],
          ['Apple Silicon / MLX', '1.8× end-to-end throughput; 2.8× faster time-to-first-token vs LM Studio', 'Corroboration on unified memory'],
          ['AMD CPU/GPU', 'Backend arm already runs on AMD Ryzen; engine port is roadmap', 'Second-source hardware'],
        ],
      },
      {
        kind: 'text',
        text: '**And it binds the buyer to no supplier.** The advantage lives in the control plane, not the accelerator kernels, which is why it ports.',
      },
    ],
  },
  {
    nav: 'TRL 6',
    kicker: 'TRL 6 — the gate is passed',
    title: 'Measured Against the Stack a Customer Runs Today',
    blocks: [
      {
        kind: 'matrix',
        head: ['At maximum tested concurrency (64)', 'Fnk AI stack vs assembled stack'],
        rows: [
          ['Completed requests per second', '**2.0× more**'],
          ['Weighted mean latency', '**1.6× lower**'],
          ['Stable-stage p95 latency', '**2.4× lower**'],
          ['Inference output-token throughput', '**1.4× higher**'],
        ],
        numeric: [1],
        minWidth: '360px',
      },
      { kind: 'node', node: <ConcurrencySweepChart /> },
      {
        kind: 'callout',
        title: 'The control arm is the slide’s real argument',
        text: 'A reactive backend still talking to vLLM over the same HTTP boundary **barely helped**. The gain appears only when capacity signalling, admission and cancellation continue *through* the inference boundary. **A competitor can adopt a reactive framework in a sprint and get almost none of this.**',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Caveats, and they are part of the result',
        text: 'A whole-stack result, not an engine benchmark. One run per point, not a confidence interval. A first-stage order effect is visible and unexplained. Engineering evidence, not an SLA. **Not yet independently reproduced** — that is the first thing certification money buys.',
      },
    ],
  },
  {
    nav: 'Harness',
    kicker: 'The Harness',
    title: 'The Configured Layer That Makes a Stack a System',
    lead: '**A model is not a product, and an agent is not an outcome.** The Fnk Harness is what turns either into a business result.',
    blocks: [
      {
        kind: 'matrix',
        head: ['What the Harness holds', ''],
        rows: [
          ['Vertical workflow packs', 'Intent-to-transaction flows per service type'],
          ['Tool and action schemas', 'Tool definitions and the action cards that carry them'],
          ['Prompt and policy packs', 'Escalation, refusal and handover thresholds; Spanish and co-official languages'],
          ['Guardrails and confirmation gates', 'Nothing that costs money executes without explicit confirmation'],
          ['Grounding configuration', 'Retrieval bound to each user’s real entitlements in the source system'],
          ['Evaluation sets', 'The customer’s own questions, rerun on every model or connector upgrade, with rollback'],
        ],
        minWidth: '480px',
      },
      {
        kind: 'text',
        text: '**It is provider-neutral** — it names a policy, never a provider — so the same pack runs on Fnk’s engine, on the customer’s hardware, or against an approved partner endpoint. It ships inside every box; for a stack-licence buyer it is priced in the licence negotiation.',
      },
    ],
  },
  {
    nav: 'Why now',
    kicker: 'Why now',
    title: 'Inference Is Leaving the Public Cloud',
    blocks: [
      {
        kind: 'metrics',
        items: [
          { value: '56% → 41%', label: 'Public cloud as the primary home for production AI inference' },
          { value: '≈56%', label: 'Enterprises now running or planning production inference on private infrastructure', tone: 'plain' },
          { value: 'In force', label: 'EU AI Act transparency obligations in force — evidence-producing deployments became a procurement requirement', tone: 'plain' },
          { value: '≈5%', label: 'OEM gross margin on AI servers — structurally short of software differentiation', tone: 'plain' },
        ],
      },
      {
        kind: 'callout',
        text: '**NVIDIA AI Enterprise lists at $4,500 per GPU per year.** The buyer already budgets software per accelerator; Fnk’s licence is priced beneath that line on every hardware class.',
      },
    ],
  },
  {
    nav: 'Pricing',
    kicker: 'Pricing',
    title: 'The Licence Is Priced Per GPU',
    lead: '**Stack licence, per GPU per year** — one list price per hardware class, the same for every buyer, set before signature and independent of any measured result. Whatever GPUs the stack saves, the saving stays with the buyer. **SaaS subscription**, monthly or annual, for teams that want the stack without running GPUs.',
    blocks: [
      {
        kind: 'matrix',
        head: ['Box component', 'Full box, year one', 'Partial box, year one', 'Recurring', 'Margin'],
        rows: [
          ['Hardware (pass-through)', '€70,000', '—', '—', '≈10%'],
          ['Licence (stack + Harness)', '€95,000', '€95,000', '€95,000', '≈90%'],
          ['Integration', '€70,000', '€55,000', '—', '≈50%'],
          ['Connectors', '€35,000', '€30,000', '€30,000 / €35,000', '≈88%'],
          ['Support', '€20,000', '€20,000', '€20,000', '≈72%'],
          ['Total', '**≈€290,000**', '**≈€200,000**', '**€145,000–€150,000**', '**≈60% / 77% Y1 · ≈87% recurring**'],
        ],
        accentRows: [5],
        numeric: [1, 2, 3, 4],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: '**The first money is a paid assessment** — €12,000–€35,000, small enough for a department budget, and the qualification instrument rather than the objective.',
      },
    ],
  },
  {
    nav: 'Market',
    kicker: 'Market',
    title: 'Every Line Needs Under One Sixth of One Percent',
    blocks: [
      {
        kind: 'matrix',
        head: ['Line', 'Addressable (EU)', 'Value per customer', 'Y5 base target', 'Share'],
        rows: [
          ['Partial box', '≈4,300', '€200k Y1 · €145k rec.', '6 customers', '0.14%'],
          ['Full box', '≈1,800', '€290k Y1 · €150k rec.', '3 customers', '0.17%'],
          ['Stack licence', '≈2,400', 'Per GPU per year', 'Not in base case', 'Upside'],
        ],
        numeric: [1, 3, 4],
      },
      {
        kind: 'text',
        text: '**The provider-scale pool alone** — ≈190 European inference providers and data-centre operators at 250–1,000 GPUs — is ≈€70m–€236m a year at list price.',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'The honest limit',
        text: 'Every filter is a management assumption on an official frame. Market share is an output of the model, not validation of it.',
      },
    ],
  },
  {
    nav: 'Competition',
    kicker: 'Competition',
    title: 'Nobody Sells the Framework and the Engine as One',
    blocks: [
      {
        kind: 'matrix',
        head: ['Group', 'Who', 'Where they stop'],
        rows: [
          ['A — Private AI infrastructure', 'HPE Private Cloud AI, Dell AI Factory, Nutanix', 'A platform; the customer still builds the application and the Harness'],
          ['B — Sovereign platforms', 'Cohere North + Aleph Alpha, IBM watsonx, Mistral', 'Priced above this band; a platform rather than a finished system'],
          ['C — Cloud knowledge AI', 'Glean, Microsoft Copilot', 'Cannot keep the data inside'],
          ['D — Open source', 'vLLM, LangChain, Onyx, Open WebUI', 'Free, genuinely good — and a permanent engineering team to own'],
          ['E — Integrators', 'Accenture, Capgemini, Minsait, Eviden', 'Bespoke, and not resellable'],
          ['F — Inference software', 'NVIDIA AI Enterprise, Red Hat AI Inference Server, Baseten', 'An engine without the framework above it, or capacity by the hour'],
        ],
        minWidth: '600px',
      },
      {
        kind: 'callout',
        tone: 'caveat',
        title: 'Where Fnk should not compete',
        text: 'Customers content for data to leave; accounts standardised on a bundled Copilot; national sovereign programmes; engineering organisations that want to own the stack.',
      },
    ],
  },
  {
    nav: 'Strategy',
    kicker: 'Strategy and go-to-market',
    title: 'References First, Then Enterprise, Then Partners',
    blocks: [
      {
        kind: 'matrix',
        head: ['Strategic choice', ''],
        rows: [
          ['Own every layer under the application', 'The gain appears only across the inference boundary — it cannot be copied one component at a time'],
          ['Sell in the unit the buyer already budgets', 'Per GPU per year, one list price per hardware class, beneath the NVIDIA AI Enterprise line'],
          ['References before enterprise', 'DialogMe in production, and paid assessments on a buyer’s own workload'],
          ['Let the buyer measure', 'The assessment ends in written acceptance thresholds; its price does not depend on the result'],
          ['Europe first, built from Spain', 'AI Act evidence, data residency and air-gapped deployment as product features'],
        ],
        minWidth: '560px',
      },
      {
        kind: 'matrix',
        head: ['Phase', 'When', 'What it is'],
        rows: [
          ['1 · References', 'Development years', 'DialogMe, the first customer, in production on the stack; **paid efficiency assessments** replaying a buyer’s workload on the Fnk stack'],
          ['2 · Enterprise, worked through events', 'Years 2–4', 'DES Málaga, Big Data & AI World, MWC/4YFN, the Huawei partner conference — twenty named targets per event, pre-booked meetings, paid-assessment proposals as the measure'],
          ['3 · Partner-led', 'Once delivery repeats', 'Integrators and OEMs, only after a second deployment in a vertical costs under 60% of the first'],
        ],
        accentRows: [0],
        minWidth: '560px',
      },
      {
        kind: 'text',
        text: '**Cold contact to signature runs 3–12 months**, longer for a first deal with no reference — which is why references come first and the box follows revenue.',
      },
    ],
  },
  {
    nav: 'The ask',
    kicker: 'The numbers and the ask',
    title: 'No External Funding Through Development, Profitable in the First Full Revenue Year',
    blocks: [
      {
        kind: 'matrix',
        head: ['Scenario', 'Development', 'First revenue', 'Year-5 revenue', 'EBITDA-positive', 'Coverage'],
        rows: [
          ['Pessimistic', '24 months', 'Year 3 — €15,000', '€665,500', 'Year 4', '1.64×'],
          ['Base', '12 months', 'Year 2 — €20,000', '€1,786,500', 'Year 3', '1.24×'],
          ['Optimistic', '6 months', 'Year 1 — €10,000', '€4,264,500', 'Year 2', '1.83×'],
        ],
        accentRows: [1],
        numeric: [3, 5],
        minWidth: '640px',
      },
      {
        kind: 'text',
        text: 'Development costs **€22,076** in year one and **€17,976** a year thereafter — **€0 of external funding in any scenario.** Base case Year 5: nine installed customers, €526,500 of recurring revenue. **The eighteen months between best and worst are worth €3,599,000 of Year-5 revenue.** Box prices 30% lower still reach Year 5 profitable, but take cash below zero on the way.',
      },
      {
        kind: 'metrics',
        items: [
          { value: '€1.2–2.0M', label: 'Seed modelled, base column — after independent reproduction and a first paying customer' },
          { value: '€5–8M', label: 'Pre-money valuation', tone: 'plain' },
          { value: '15–22%', label: 'Implied dilution', tone: 'plain' },
          { value: '18–30 mo', label: 'Runway at the resulting cost base', tone: 'plain' },
        ],
      },
      {
        kind: 'callout',
        title: 'Not yet — and where this could go',
        text: 'The recommendation of record is not to raise during development. Engineering 45%, delivery capacity 17.5%, go-to-market 15%, benchmark and certification 7.5%, working capital 10%, reserve 5%. The modelled exit is €8m–€16m. Beyond it sits **the infrastructure standard** — the framework and engine a data centre standardises on and an OEM ships preinstalled. It is in no number here. Contact: [investors@fnk.ai](mailto:investors@fnk.ai).',
      },
    ],
  },
];
